/**
 * PreviewAdminOrchestrator — pixel-faithful render of the ClaimSync "Admin
 * Orchestrator" template (Daylight), with a light/dark toggle.
 *
 * The pipeline-run list and the top metrics derive from REAL claims
 * (/api/v1/reports), grouped by status into the 14-stage pipeline: each claim's
 * status maps to a stage + bar, and the metric tiles count those real groups
 * (total runs, running, complete). Orchestration internals — the LLM approval
 * queue, per-engine system health, the activity feed, and throughput numbers —
 * are the template's representative static content (these live behind internal
 * orchestration APIs a design-review surface can't reach). This is clearly a
 * preview, not the live admin console.
 *
 * Mounted at /preview/admin-orchestrator. Self-contained — touches no real
 * admin route, feature flag, or working surface.
 *
 * Black + orange discipline; green ("done"/"healthy") only on complete/healthy
 * states. "Running"/"blocked"/"stale"/"attention" states are ORANGE, never
 * red/yellow.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import {
  SERIF,
  BODY,
  MONO,
  PREVIEW_ZOOM,
  Palette,
  ChipKind,
  useTheme,
  ThemeToggle,
  chipStyle,
  fmtUSD,
  relTime,
} from './preview/previewTheme';

interface ApiReport {
  id: string;
  client_name?: string;
  status?: string;
  result?: { final_claim_amount?: number };
  created_at?: string;
  updated_at?: string;
}

type RunStatus = 'Blocked' | 'Running' | 'Complete';

interface Run {
  id?: string;
  client: string;
  stage: number;
  label: string;
  status: RunStatus;
  amount?: number;
  updatedAt?: string;
}

// ---- status → pipeline stage mapping (14-stage pipeline) ----
const STAGE_LABELS = [
  'Intake',
  'Data ingestion',
  'Document OCR',
  'Classification',
  'Anomaly detection',
  'Reconciliation',
  'Forensic reconstruction',
  'Continuation modeling',
  'Methodology review',
  'Attestation gate',
  'Settlement modeling',
  'Demand generation',
  'Attorney review',
  'Complete',
];

function runFromStatus(s?: string): { stage: number; label: string; status: RunStatus } {
  const v = (s || '').toLowerCase();
  if (v === 'completed' || v === 'complete' || v === 'finalized')
    return { stage: 14, label: 'Complete', status: 'Complete' };
  if (v === 'blocked' || v === 'error' || v === 'failed' || v === 'on_hold')
    return { stage: 11, label: 'Attestation gate', status: 'Blocked' };
  if (v === 'review' || v === 'in_review' || v === 'pending_review')
    return { stage: 13, label: 'Attorney review', status: 'Running' };
  if (v === 'analyzing' || v === 'in_progress' || v === 'processing')
    return { stage: 8, label: 'Forensic reconstruction', status: 'Running' };
  if (v === 'draft' || v === 'new' || v === 'intake')
    return { stage: 3, label: 'Data ingestion', status: 'Running' };
  return { stage: 6, label: 'Reconciliation', status: 'Running' };
}

// ---- representative content (template) ----
interface Approval {
  action: string;
  claim: string;
  model: string;
  conf: number;
}
const APPROVALS: Approval[] = [
  { action: 'Approve forensic narrative · Engine Review', claim: 'Riverside Kitchen & Tap', model: 'Claude Opus', conf: 91 },
  { action: 'Approve reclass · 2 owner-draw transactions', claim: 'Brazos Freight Co.', model: 'GPT-4o', conf: 84 },
  { action: 'Approve methodology · Variable-profit', claim: 'Hill Country Grocers', model: 'Claude Opus', conf: 96 },
  { action: 'Approve demand draft · CA type 3', claim: 'Gulf Coast Dental', model: 'Claude Opus', conf: 89 },
];

interface HealthRow {
  label: string;
  value: string;
  dotColor: string;
  fg: string;
}

interface ActivityRow {
  text: string;
  dotColor: string;
  time: string;
}
const ACTIVITY: ActivityRow[] = [
  { text: 'Continuation-rate engine re-run on Riverside', dotColor: 'orange', time: '09:52:14' },
  { text: 'Methodology approved · Hill Country', dotColor: 'green', time: '09:48:03' },
  { text: 'Batch OCR completed · 9 documents', dotColor: 'green', time: '09:41:55' },
  { text: 'Stale-version flag raised · continuation v2.2', dotColor: 'orange', time: '09:39:20' },
];

// ---- representative fallback runs (used when no real reports load) ----
const FALLBACK_RUNS: Run[] = [
  { client: 'Riverside Kitchen & Tap', stage: 11, label: 'Attestation gate', status: 'Blocked' },
  { client: 'Gulf Coast Dental', stage: 13, label: 'Demand generation', status: 'Running' },
  { client: 'Hill Country Grocers', stage: 8, label: 'Forensic reconstruction', status: 'Running' },
  { client: 'Brazos Freight Co.', stage: 6, label: 'Anomaly detection', status: 'Running' },
  { client: 'Prairie Press Printing', stage: 3, label: 'Data ingestion', status: 'Running' },
  { client: 'Lone Star Auto Body', stage: 14, label: 'Complete', status: 'Complete' },
];

export default function PreviewAdminOrchestrator() {
  const { theme, P, toggle } = useTheme();
  const navigate = useNavigate();
  const [reports, setReports] = useState<ApiReport[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.get('/api/v1/reports?limit=200', { showErrorToast: false });
        const list: ApiReport[] = Array.isArray(data?.reports) ? data.reports : Array.isArray(data) ? data : [];
        if (alive) setReports(list);
      } catch {
        if (alive) setReports([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Real claims → pipeline runs (most recently updated first).
  const runs = useMemo<Run[]>(() => {
    if (!reports.length) return FALLBACK_RUNS;
    const sorted = [...reports].sort(
      (a, b) =>
        new Date(b.updated_at || b.created_at || 0).getTime() -
        new Date(a.updated_at || a.created_at || 0).getTime()
    );
    return sorted.slice(0, 8).map((r) => {
      const mapped = runFromStatus(r.status);
      return {
        id: r.id,
        client: r.client_name || `Claim ${(r.id || '').slice(0, 8).toUpperCase()}`,
        stage: mapped.stage,
        label: mapped.label,
        status: mapped.status,
        amount: r.result?.final_claim_amount,
        updatedAt: r.updated_at || r.created_at,
      };
    });
  }, [reports]);

  const runningCount = runs.filter((r) => r.status === 'Running').length;
  const blockedCount = runs.filter((r) => r.status === 'Blocked').length;
  const completeCount = runs.filter((r) => r.status === 'Complete').length;
  const totalRuns = runs.length;

  const metrics: { label: string; value: string; sub: string; accent: string; valueColor: string }[] = [
    {
      label: 'Pipeline runs',
      value: String(totalRuns),
      sub: `${runningCount} running · ${blockedCount} blocked`,
      accent: P.accentNeutral,
      valueColor: P.textPrimary,
    },
    {
      label: 'Awaiting approval',
      value: String(APPROVALS.length),
      sub: 'human-in-the-loop gate',
      accent: P.orange,
      valueColor: P.orangeText,
    },
    {
      label: 'Engines healthy',
      value: '14/14',
      sub: '1 stale flagged',
      accent: P.greenSolid,
      valueColor: P.greenText,
    },
    {
      label: 'Throughput (24h)',
      value: '31',
      sub: 'claims advanced',
      accent: P.accentNeutral,
      valueColor: P.textPrimary,
    },
  ];

  const health: HealthRow[] = [
    { label: 'Engine fleet', value: '14/14', dotColor: P.greenSolid, fg: P.greenText },
    { label: 'Stale runs', value: '1', dotColor: P.orange, fg: P.orangeText },
    { label: 'Queue depth', value: '23 jobs', dotColor: P.textGhost, fg: P.textBody },
    { label: 'Error rate (24h)', value: '0.4%', dotColor: P.greenSolid, fg: P.greenText },
    { label: 'Deploy', value: 'v4.91.0', dotColor: P.textGhost, fg: P.textBody },
  ];

  const valBase: React.CSSProperties = {
    fontFamily: MONO,
    fontSize: 26,
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
  };

  const confColor = (p: number): string => (p < 85 ? P.orangeText : P.greenText);

  const runChipKind = (s: RunStatus): ChipKind =>
    s === 'Blocked' ? 'neutral' : s === 'Complete' ? 'green' : 'orange';

  const navGroups: { group: string; items: { label: string; icon: string; badge?: string; active?: boolean }[] }[] = [
    {
      group: 'Operations',
      items: [
        { label: 'Orchestrator', icon: '◧', active: true },
        { label: 'Approvals', icon: '✺', badge: String(APPROVALS.length) },
        { label: 'Issue Tracker', icon: '◬' },
      ],
    },
    {
      group: 'System',
      items: [
        { label: 'Engine Fleet', icon: '⊞' },
        { label: 'Batch Jobs', icon: '▦' },
        { label: 'Activity Log', icon: '≣' },
      ],
    },
    {
      group: 'Admin',
      items: [
        { label: 'Users & Roles', icon: '◴' },
        { label: 'Pattern Admin', icon: '§' },
        { label: 'Settings', icon: '◵' },
      ],
    },
  ];

  return (
    <div
      style={{
        zoom: PREVIEW_ZOOM,
        display: 'flex',
        height: '100vh',
        width: '100%',
        overflow: 'hidden',
        background: P.pageBg,
        fontFamily: BODY,
        color: P.textBody,
      }}
    >
      {/* ============ LEFT RAIL ============ */}
      <aside
        style={{
          width: 240,
          flex: '0 0 240px',
          background: P.pageBg,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          borderRight: `1px solid ${P.railHairline}`,
        }}
      >
        <div style={{ padding: '20px 20px 16px', display: 'flex', alignItems: 'center', gap: 11 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: P.orange,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 30px',
            }}
          >
            <div style={{ width: 12, height: 12, border: '2.5px solid #fefefe', borderRadius: '50%' }} />
          </div>
          <div style={{ lineHeight: 1 }}>
            <div style={{ fontFamily: SERIF, color: P.textPrimary, fontSize: 20, letterSpacing: '0.2px' }}>The Figure Co.</div>
            <div
              style={{
                fontSize: 9.5,
                letterSpacing: '1.4px',
                color: P.textMuted,
                textTransform: 'uppercase',
                marginTop: 2,
              }}
            >
              Operations
            </div>
          </div>
        </div>

        <nav style={{ flex: 1, overflowY: 'auto', padding: '6px 0 16px' }}>
          {navGroups.map((grp) => (
            <div key={grp.group} style={{ margin: '10px 0 2px' }}>
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: '1.3px',
                  color: P.textMuted,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  padding: '6px 22px 4px',
                }}
              >
                {grp.group}
              </div>
              {grp.items.map((item) => (
                <a
                  key={item.label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 11,
                    padding: '8px 18px 8px 19px',
                    fontSize: 13.5,
                    fontWeight: 500,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    borderLeft: `3px solid ${item.active ? P.orange : 'transparent'}`,
                    color: item.active ? P.textPrimary : P.textLabel,
                    background: item.active ? P.elevBg : 'transparent',
                  }}
                >
                  <span style={{ fontSize: 14, width: 18, textAlign: 'center', opacity: 0.9 }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.badge && (
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 600,
                        background: P.orange,
                        color: P.onOrange,
                        borderRadius: 999,
                        padding: '1px 7px',
                        minWidth: 18,
                        textAlign: 'center',
                      }}
                    >
                      {item.badge}
                    </span>
                  )}
                </a>
              ))}
            </div>
          ))}
        </nav>

        <div
          style={{
            padding: '12px 16px',
            borderTop: `1px solid ${P.railHairline}`,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: P.elevBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: P.textBody,
              fontWeight: 600,
              fontSize: 12.5,
              flex: '0 0 32px',
            }}
          >
            DW
          </div>
          <div style={{ flex: 1, lineHeight: 1.25, minWidth: 0 }}>
            <div style={{ color: P.textPrimary, fontSize: 13, fontWeight: 500 }}>Dana Whitfield</div>
            <div style={{ color: P.textLabel, fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: P.orange, display: 'inline-block' }} />
              Admin · Operations
            </div>
          </div>
          <span style={{ color: P.textMuted, fontSize: 15 }}>⌄</span>
        </div>
      </aside>

      {/* ============ MAIN ============ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
        <header
          style={{
            height: 58,
            flex: '0 0 58px',
            background: P.pageBg,
            borderBottom: `1px solid ${P.railHairline}`,
            display: 'flex',
            alignItems: 'center',
            padding: '0 22px',
            gap: 14,
          }}
        >
          <div style={{ fontSize: 13, color: P.textPrimary, fontWeight: 500 }}>Orchestrator</div>
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: P.elevBg,
              border: `1px solid ${P.cardBorder}`,
              borderRadius: 999,
              padding: '5px 12px',
              fontSize: 12,
              color: P.greenText,
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: P.greenSolid, display: 'inline-block' }} />
            Pipeline healthy
          </div>
          <button
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              border: `1px solid ${P.cardBorder}`,
              background: P.elevBg,
              color: P.textBody,
              fontSize: 15,
              cursor: 'pointer',
            }}
          >
            ◷
          </button>
          <ThemeToggle P={P} theme={theme} onToggle={toggle} />
        </header>

        <main style={{ flex: 1, overflow: 'auto', background: P.pageBg }}>
          <div style={{ padding: '28px 32px 48px', maxWidth: 1360, margin: '0 auto' }}>
            {/* PAGE HEADER */}
            <div style={{ marginBottom: 22 }}>
              <div
                style={{
                  fontSize: 12,
                  letterSpacing: '1.4px',
                  color: P.orangeText,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  marginBottom: 6,
                }}
              >
                Admin · Orchestrator
              </div>
              <h1 style={{ fontFamily: SERIF, fontSize: 32, color: P.textPrimary, margin: 0, fontWeight: 400 }}>Pipeline</h1>
              <p style={{ margin: '6px 0 0', color: P.textLabel, fontSize: 14.5 }}>
                <strong style={{ color: P.orangeText }}>{APPROVALS.length} actions</strong> await your approval · 14-stage
                pipeline · LLMs interpret, code calculates.
              </p>
            </div>

            {/* METRIC TILES */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
              {metrics.map((m) => (
                <div
                  key={m.label}
                  style={{
                    background: P.cardBg,
                    border: `1px solid ${P.cardBorder}`,
                    borderLeft: `3px solid ${m.accent}`,
                    borderRadius: 13,
                    padding: '18px 20px',
                  }}
                >
                  <div style={{ fontSize: 12.5, color: P.textLabel, fontWeight: 500, marginBottom: 10 }}>{m.label}</div>
                  <div style={{ ...valBase, color: m.valueColor }}>{m.value}</div>
                  <div style={{ fontSize: 11.5, color: P.textMuted, marginTop: 7 }}>{m.sub}</div>
                </div>
              ))}
            </div>

            {/* TWO-COLUMN BODY */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                {/* LLM APPROVAL QUEUE */}
                <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
                  <div
                    style={{
                      padding: '16px 20px',
                      borderBottom: `1px solid ${P.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                    }}
                  >
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: P.orange,
                        animation: 'pulse 1.4s ease-in-out infinite',
                      }}
                    />
                    <h2
                      style={{
                        fontFamily: SERIF,
                        fontSize: 18,
                        color: P.textPrimary,
                        margin: 0,
                        fontWeight: 400,
                        flex: 1,
                      }}
                    >
                      LLM Approval Queue
                    </h2>
                    <span style={{ fontSize: 12, color: P.textLabel }}>human-in-the-loop</span>
                  </div>
                  {APPROVALS.map((a) => (
                    <div
                      key={a.action}
                      style={{
                        padding: '15px 20px',
                        borderBottom: `1px solid ${P.rowDivider}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, color: P.textPrimary, fontWeight: 500 }}>{a.action}</div>
                        <div style={{ fontSize: 11.5, color: P.textMuted, marginTop: 3 }}>
                          {a.claim} · <span style={{ fontFamily: MONO, color: P.textLabel }}>{a.model}</span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                        <div style={{ fontFamily: MONO, fontSize: 14, fontWeight: 500, color: confColor(a.conf) }}>
                          {a.conf}%
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            color: P.textMuted,
                          }}
                        >
                          confidence
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 7, flex: '0 0 auto' }}>
                        <button
                          style={{
                            background: P.orange,
                            color: P.onOrange,
                            border: 'none',
                            borderRadius: 8,
                            padding: '8px 14px',
                            fontSize: 12.5,
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontFamily: BODY,
                          }}
                        >
                          Approve
                        </button>
                        <button
                          style={{
                            background: 'transparent',
                            color: P.textLabel,
                            border: `1px solid ${P.controlBorder}`,
                            borderRadius: 8,
                            padding: '8px 12px',
                            fontSize: 12.5,
                            fontWeight: 500,
                            cursor: 'pointer',
                            fontFamily: BODY,
                          }}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* PIPELINE RUNS (REAL claims) */}
                <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: `1px solid ${P.divider}` }}>
                    <h2 style={{ fontFamily: SERIF, fontSize: 18, color: P.textPrimary, margin: 0, fontWeight: 400 }}>
                      Pipeline Runs
                    </h2>
                  </div>
                  {runs.map((r, idx) => {
                    const pct = `${Math.round((r.stage / 14) * 100)}%`;
                    const barColor = r.status === 'Complete' ? P.greenSolid : P.orange;
                    const clickable = !!r.id;
                    return (
                      <div
                        key={r.id || `${r.client}-${idx}`}
                        onClick={clickable ? () => navigate(`/report/${r.id}`) : undefined}
                        style={{
                          padding: '14px 20px',
                          borderBottom: `1px solid ${P.rowDivider}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 16,
                          cursor: clickable ? 'pointer' : 'default',
                        }}
                      >
                        <div style={{ flex: '0 0 200px', minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              color: P.textPrimary,
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {r.client}
                          </div>
                          <div style={{ fontSize: 11, color: P.textMuted, marginTop: 2 }}>
                            Stage {r.stage}/14 · {r.label}
                            {typeof r.amount === 'number' && r.amount > 0 ? ` · ${fmtUSD(r.amount)}` : ''}
                            {r.updatedAt ? ` · ${relTime(r.updatedAt)}` : ''}
                          </div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ height: 5, background: P.divider, borderRadius: 999, overflow: 'hidden' }}>
                            <div style={{ height: '100%', borderRadius: 999, width: pct, background: barColor }} />
                          </div>
                        </div>
                        <span
                          style={{
                            fontFamily: MONO,
                            fontSize: 12,
                            color: P.textLabel,
                            flex: '0 0 auto',
                            width: 48,
                            textAlign: 'right',
                          }}
                        >
                          {r.stage}/14
                        </span>
                        <span style={{ ...chipStyle(runChipKind(r.status), P), flex: '0 0 auto' }}>{r.status}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* RIGHT: HEALTH + ACTIVITY */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: `1px solid ${P.divider}` }}>
                    <h3 style={{ fontFamily: SERIF, fontSize: 16, color: P.textPrimary, margin: 0, fontWeight: 400 }}>
                      System Health
                    </h3>
                  </div>
                  {health.map((h) => (
                    <div
                      key={h.label}
                      style={{
                        padding: '12px 18px',
                        borderBottom: `1px solid ${P.rowDivider}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ fontSize: 13, color: P.textLabel }}>{h.label}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: h.dotColor, display: 'inline-block' }} />
                        <span style={{ fontFamily: MONO, fontSize: 13, fontWeight: 500, color: h.fg }}>{h.value}</span>
                      </span>
                    </div>
                  ))}
                </div>

                <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: `1px solid ${P.divider}` }}>
                    <h3 style={{ fontFamily: SERIF, fontSize: 16, color: P.textPrimary, margin: 0, fontWeight: 400 }}>
                      Activity
                    </h3>
                  </div>
                  {ACTIVITY.map((a, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '11px 18px',
                        borderBottom: `1px solid ${P.rowDivider}`,
                        display: 'flex',
                        gap: 11,
                        alignItems: 'flex-start',
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: a.dotColor === 'green' ? P.greenSolid : P.orange,
                          flex: '0 0 8px',
                          marginTop: 4,
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, color: P.textBody, lineHeight: 1.35 }}>{a.text}</div>
                        <div style={{ fontSize: 10.5, color: P.textMuted, fontFamily: MONO, marginTop: 2 }}>{a.time}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
